/**
 * Shared shape for the paginated list endpoints (`@fastn/http` list-query).
 *
 * The four lists that page against the API — connectors, actions, workflows,
 * workflow executions — are served by two different services. This module is
 * the client half of that one contract, so a page never has to know which
 * service it is talking to in order to render a pager.
 */

export interface ListParams {
  /** Settled search needle. Omit or leave undefined for "no filter". */
  search?: string;
  /** Rows per page. Omit for "every row" (endpoints treat pagination as opt-in). */
  limit?: number;
  offset?: number;
}

export interface PaginationMeta {
  total: number;
  limit: number;
  offset: number;
  /**
   * `total` is a floor: the endpoint stopped counting at a cap rather than
   * scanning an unbounded table. Only the executions list sets it.
   */
  totalIsLowerBound?: boolean;
}

/**
 * A facet count for one value of a filter, counted over the set the OTHER
 * filters left — so the counts still say where to go next once a chip is on.
 */
export interface FacetCount {
  key: string;
  count: number;
}

/** Envelope every paginated list endpoint returns. */
export interface ListResponse<T> {
  data: T[];
  pagination?: PaginationMeta;
  /** Connector browse-category facets. Sent by the connectors list, and by the workflows list when grouping by category. */
  categories?: FacetCount[];
}

/**
 * Append list params to a URLSearchParams.
 *
 * `offset` is written even when 0 so that the presence of pagination is
 * explicit: the endpoints treat "neither limit nor offset" as "return
 * everything", and a caller that asked for page 1 of 25 must not silently get
 * the unbounded list back if `limit` is ever dropped upstream.
 */
export function appendListParams(qs: URLSearchParams, params: ListParams): URLSearchParams {
  const { search, limit, offset } = params;
  if (search) qs.set("search", search);
  if (limit !== undefined) {
    qs.set("limit", String(limit));
    qs.set("offset", String(offset ?? 0));
  } else if (offset !== undefined && offset > 0) {
    qs.set("offset", String(offset));
  }
  return qs;
}

/**
 * Normalise a list response into rows + a trustworthy total.
 *
 * The total falls back to the row count only when the server sent no
 * `pagination` block at all — an older deploy, or an endpoint that has not been
 * converted. Falling back per-page instead would make the pager claim the list
 * ends at the bottom of page one.
 */
export function unwrapList<T>(
  response: ListResponse<T> | T[] | undefined,
): { rows: T[]; total: number; totalIsLowerBound: boolean; categories: FacetCount[] } {
  if (Array.isArray(response)) return { rows: response, total: response.length, totalIsLowerBound: false, categories: [] };
  const rows = response?.data ?? [];
  return {
    rows,
    total: response?.pagination?.total ?? rows.length,
    totalIsLowerBound: response?.pagination?.totalIsLowerBound ?? false,
    categories: response?.categories ?? [],
  };
}

/**
 * Is any filter narrowing this list?
 *
 * An empty list means two different things and the difference is the whole
 * message: "nothing matches your filter" (offer to clear it) versus "there is
 * nothing here yet" (explain what will make something appear). The executions
 * list showed the filter wording unconditionally, so a workspace that had simply
 * never run a workflow was told its filter was too narrow — and the "Show all
 * runs" button reset filters that were already at their defaults, which is
 * literally a no-op.
 *
 * Pass every filter that narrows the query. A value counts as active when it is
 * a non-empty string that is not the "no filter" sentinel — `undefined`, `""`
 * and `"All"` all mean unfiltered across these pages.
 */
export function hasActiveListFilter(...values: Array<string | undefined | null>): boolean {
  return values.some((v) => {
    if (v === undefined || v === null) return false;
    const text = v.trim();
    return text !== "" && text !== "All";
  });
}
